import {
    ApiOutlined,
    AuditOutlined,
    BookOutlined,
    ClusterOutlined,
    DatabaseOutlined,
    FileSearchOutlined,
    FolderOutlined,
    HomeOutlined,
    MedicineBoxOutlined,
    ProjectOutlined,
    ReadOutlined,
    RobotOutlined,
    SearchOutlined,
    SecurityScanOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Routes } from '@packages/enums';
import { useAppSelector } from '@ui/app/store';
import { Icon } from '@ui/assets/icons/IconWrapper';
import { AppMode } from '@ui/constants';
import { selectIsSystemAdmin } from '@ui/store/slices/userSlice';
import { useAppMode } from '@ui/hooks';
import type { MenuProps } from 'antd';
import { Tooltip } from 'antd';
import { useMemo } from 'react';
import { MenuItemWithContext } from '../../common/ContextMenuItem';

export const useSidebarMenu = (collapsed: boolean) => {
    const isAdmin = useAppSelector(selectIsSystemAdmin);
    const mode = useAppMode();

    return useMemo<MenuProps['items']>(() => {
        const itemsByMode: Record<AppMode, MenuProps['items']> = {
            [AppMode.PATIENT_REGISTRY]: [
                {
                    key: '/projects',
                    title: '',
                    icon: (
                        <ProjectOutlined
                            style={{ fontSize: '18px' }}
                            className="text-white"
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route="/projects"
                            tooltip="Build, analyze & compare patient cohorts with no-code rules and reusable filters."
                        >
                            <div className="h-full w-full">Projects</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: '/integrations',
                    title: '',
                    icon: (
                        <ApiOutlined
                            style={{ fontSize: '18px' }}
                            className="text-white"
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route="/integrations"
                            tooltip="Manage Integrations"
                        >
                            <div className="h-full w-full">Integrations</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: '/case-finding',
                    title: '',
                    icon: (
                        <SearchOutlined
                            style={{ fontSize: '18px' }}
                            className="text-white"
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route="/case-finding"
                            tooltip="Case Finding"
                        >
                            <div className="h-full w-full">Case Finding</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: Routes.REGISTRY_STANDARDS,
                    title: '',
                    icon: (
                        <FileSearchOutlined
                            style={{ fontSize: '18px' }}
                            className="text-white"
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.REGISTRY_STANDARDS}
                            tooltip="Browse registry standards, clinical rulesets, and jurisdictions"
                        >
                            <div className="h-full w-full">Registry Standards</div>
                        </MenuItemWithContext>
                    ),
                },
            ],
            [AppMode.PATIENT_JOURNEY]: [
                {
                    key: Routes.HOME,
                    title: '',
                    icon: (
                        <HomeOutlined
                            style={{ fontSize: '18px' }}
                            className="text-white"
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.HOME}
                            tooltip={
                                collapsed
                                    ? 'Home'
                                    : 'Monitor key patient, document, and imaging metrics with interactive charts and real-time trends.'
                            }
                        >
                            <div className="h-full w-full">Home</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: 'data-integration-submenu',
                    icon: <ClusterOutlined style={{ fontSize: '18px' }} />,
                    label: 'Data Integration',
                    children: [
                        {
                            key: Routes.SOURCE_REGISTRY,
                            icon: (
                                <Icon
                                    name="DataSourceIconSidebar"
                                    size={20}
                                />
                            ),
                            label: (
                                <MenuItemWithContext
                                    route={Routes.SOURCE_REGISTRY}
                                    tooltip="Connect, configure, and monitor all external data sources feeding into the PJI platform."
                                >
                                    <span>Source Registry</span>
                                </MenuItemWithContext>
                            ),
                        },
                        {
                            key: Routes.DATA_INGESTION,
                            title: '',
                            icon: (
                                <Icon
                                    name="DataIngestionIconSidebar"
                                    size={20}
                                />
                            ),
                            label: (
                                <MenuItemWithContext
                                    route={Routes.DATA_INGESTION}
                                    tooltip="Trigger and monitor data ingestion pipelines from all connected sources."
                                >
                                    <span>Data Ingestion</span>
                                </MenuItemWithContext>
                            ),
                        },
                        {
                            key: Routes.DB_EXPLORER,
                            title: '',
                            icon: <DatabaseOutlined style={{ fontSize: '20px' }} />,
                            label: (
                                <MenuItemWithContext
                                    route={Routes.DB_EXPLORER}
                                    tooltip="Explore the database using natural-language queries and get instant insights."
                                >
                                    <span>Database Explorer</span>
                                </MenuItemWithContext>
                            ),
                        },
                        {
                            key: Routes.DATASETS,
                            title: '',
                            icon: <FolderOutlined style={{ fontSize: '20px' }} />,
                            label: (
                                <MenuItemWithContext
                                    route={Routes.DATASETS}
                                    tooltip="Create and manage datasets containing structured, unstructured, and imaging data."
                                >
                                    <span>Datasets</span>
                                </MenuItemWithContext>
                            ),
                        },
                        {
                            key: Routes.REGISTRY_STANDARDS,
                            title: '',
                            icon: <FileSearchOutlined style={{ fontSize: '20px' }} />,
                            label: (
                                <MenuItemWithContext
                                    route={Routes.REGISTRY_STANDARDS}
                                    tooltip="Browse registry standards, clinical rulesets, and jurisdictions"
                                >
                                    <span>Registry Standards</span>
                                </MenuItemWithContext>
                            ),
                        },
                        ...(isAdmin
                            ? [
                                  {
                                      key: Routes.KB_MANAGEMENT,
                                      title: '',
                                      icon: <BookOutlined style={{ fontSize: '16px' }} />,
                                      label: (
                                          <MenuItemWithContext
                                              route={Routes.KB_MANAGEMENT}
                                              tooltip="Manage Knowledge Base (VDB) entries, definitions, and SQL queries."
                                          >
                                              <span>KB Management</span>
                                          </MenuItemWithContext>
                                      ),
                                  },
                              ]
                            : []),
                    ],
                },
                {
                    key: Routes.CLINICAL_MEASURES,
                    title: '',
                    icon: (
                        <Icon
                            name="CalculationIconSidebar"
                            size={20}
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.CLINICAL_MEASURES}
                            tooltip={
                                collapsed
                                    ? 'Clinical Measures'
                                    : 'Compute patient-level healthcare measures, safety indicators, and derived clinical scores.'
                            }
                        >
                            <div className="h-full w-full">Clinical Measures</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: Routes.COHORTS,
                    title: '',
                    icon: (
                        <Icon
                            name="CohortsIconSidebar"
                            size={22}
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.COHORTS}
                            tooltip={
                                collapsed
                                    ? 'Cohort Builder'
                                    : 'Build, analyze, and compare patient cohorts with no-code rules and reusable filters.'
                            }
                        >
                            <div className="h-full w-full">Cohort Builder</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: Routes.PATIENTS,
                    title: '',
                    icon: (
                        <Icon
                            name="PatientIconSidebar"
                            size={20}
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.PATIENTS}
                            tooltip={
                                collapsed
                                    ? 'Patient Journey'
                                    : "View a complete, longitudinal timeline of each patient's encounters, labs, diagnoses, and treatments."
                            }
                        >
                            <div className="h-full w-full">Patient Journey</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: Routes.CHAT,
                    title: '',
                    icon: (
                        <Icon
                            name="GroupedChat"
                            size={24}
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.CHAT}
                            tooltip={
                                collapsed
                                    ? 'CoPilot'
                                    : 'Start a new conversation with the CoPilot to query, analyze, and summarize clinical data.'
                            }
                        >
                            <div className="h-full w-full">CoPilot</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: 'agents-submenu',
                    icon: <RobotOutlined style={{ fontSize: '18px' }} />,
                    label: 'Agents',
                    children: [
                        {
                            key: Routes.CPG_RAG,
                            icon: <ReadOutlined style={{ fontSize: '18px' }} />,
                            label: (
                                <MenuItemWithContext
                                    route={Routes.CPG_RAG}
                                    tooltip="Query clinical practice guidelines using AI-powered retrieval augmented generation."
                                >
                                    <span>CPG RAG</span>
                                </MenuItemWithContext>
                            ),
                        },
                        {
                            key: Routes.MCP_DOCS,
                            icon: <BookOutlined style={{ fontSize: '18px' }} />,
                            label: (
                                <MenuItemWithContext
                                    route={Routes.MCP_DOCS}
                                    tooltip="View MCP (Model Context Protocol) documentation for Healthcare NLP servers."
                                >
                                    <span>MCP Docs</span>
                                </MenuItemWithContext>
                            ),
                        },
                    ],
                },
                {
                    key: Routes.DATA_CURATION,
                    icon: (
                        <Icon
                            name="DataCurationIconSidebar"
                            size={20}
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.DATA_CURATION}
                            tooltip="Validate, enrich, and align extracted data using ontology-driven curation and human review."
                        >
                            <span>Data Curation Studio</span>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: Routes.DE_IDENTIFICATION,
                    icon: <SecurityScanOutlined style={{ fontSize: '18px' }} />,
                    label: (
                        <MenuItemWithContext
                            route={Routes.DE_IDENTIFICATION}
                            tooltip="View, detect, anonymize, and validate PHI in imaging files including DICOM, pathology slides, and other image formats."
                        >
                            <span>De-Identification</span>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: Routes.TERMINOLOGY_SERVER,
                    title: '',
                    icon: <MedicineBoxOutlined style={{ fontSize: '18px' }} />,
                    label: (
                        <MenuItemWithContext
                            route={Routes.TERMINOLOGY_SERVER}
                            tooltip="Look up standard medical codes from text phrases using the Terminology Server."
                        >
                            <div className="h-full w-full">Terminology Server</div>
                        </MenuItemWithContext>
                    ),
                },
                ...(isAdmin
                    ? [
                          {
                              key: Routes.AUDIT,
                              title: '',
                              icon: <AuditOutlined style={{ fontSize: '16px' }} />,
                              label: (
                                  <MenuItemWithContext
                                      route={Routes.AUDIT}
                                      tooltip={
                                          collapsed
                                              ? 'Audit Logs'
                                              : 'View and filter system audit logs tracking all user actions and API calls.'
                                      }
                                  >
                                      Audit Logs
                                  </MenuItemWithContext>
                              ),
                          },
                      ]
                    : []),
                ...(isAdmin
                    ? [
                          {
                              key: Routes.ADMIN,
                              title: '',
                              icon: (
                                  <Tooltip
                                      title={collapsed ? 'Administration' : 'User and role management'}
                                      placement="right"
                                      mouseLeaveDelay={0}
                                  >
                                      <UserOutlined style={{ fontSize: '16px' }} />
                                  </Tooltip>
                              ),
                              label: (
                                  <MenuItemWithContext
                                      route={Routes.ADMIN}
                                      tooltip={collapsed ? 'Administration' : 'User and role management'}
                                  >
                                      Administration
                                  </MenuItemWithContext>
                              ),
                          },
                      ]
                    : []),
                {
                    key: Routes.LICENSE,
                    title: '',
                    icon: (
                        <Icon
                            name="LicenseIconSidebar"
                            size={20}
                        />
                    ),
                    label: (
                        <MenuItemWithContext
                            route={Routes.LICENSE}
                            tooltip={
                                collapsed
                                    ? 'License'
                                    : 'Import, manage, and validate product licenses to activate platform features and services.'
                            }
                        >
                            <div className="h-full w-full">License</div>
                        </MenuItemWithContext>
                    ),
                },
                {
                    key: 'documentation',
                    title: '',
                    icon: <BookOutlined style={{ fontSize: '18px' }} />,
                    label: (
                        <Tooltip
                            title={
                                collapsed
                                    ? 'Documentation'
                                    : 'Access comprehensive documentation and guides for the Patient Journey Intelligence platform.'
                            }
                            placement="right"
                            mouseEnterDelay={0.3}
                            mouseLeaveDelay={0}
                        >
                            <div
                                className="block h-full w-full cursor-pointer"
                                onClick={() => {
                                    // Always use /docs/ - server will proxy to Docusaurus in dev or serve static files in prod
                                    window.open('/docs/', '_blank');
                                }}
                            >
                                {collapsed ? '' : <div className="h-full w-full">Documentation</div>}
                            </div>
                        </Tooltip>
                    ),
                },
            ],
        };

        return itemsByMode[mode];
    }, [mode, collapsed, isAdmin]);
};
